import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FileText, Download, ArrowRight, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { api } from '../lib/api';
import { openPdf } from '../lib/downloads';
import { t } from '../i18n';
import { isUserLoggedIn } from '../lib/auth';

export default function History({ lang }) {
  const nav = useNavigate();
  const [loading, setLoading] = useState(false);
  const [reports, setReports] = useState([]);

  const groupedReports = reports.reduce((groups, report) => {
    const key = report.created_at?.slice(0, 10) || 'Undated';
    const todayKey = new Date().toISOString().slice(0, 10);
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    const label = key === todayKey ? 'Today' : key === yesterday.toISOString().slice(0, 10) ? 'Yesterday' : key;
    groups[label] = groups[label] || [];
    groups[label].push(report);
    return groups;
  }, {});

  const fetch = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/reports/me');
      setReports(data.reports);
    } catch (e) {
      setReports([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isUserLoggedIn()) return nav('/login');
    fetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10 sm:py-16 fade-up">
      <h1 className="font-display text-4xl sm:text-5xl text-[#1A1A1A]" data-testid="history-title">{t(lang, 'my_reports')}</h1>
      <p className="text-[#4A4A4A] mt-3 text-lg">Your personal dashboard records</p>

      {loading ? (
        <div className="mt-10 text-center text-[#7A7A7A]"><Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" />Loading your records...</div>
      ) : reports.length === 0 ? (
        <div className="mt-10 text-center text-[#7A7A7A]" data-testid="history-empty">{t(lang, 'no_reports')}</div>
      ) : (
        <div className="space-y-8 mt-8" data-testid="history-list">
          {Object.entries(groupedReports).map(([dateLabel, items]) => (
            <section key={dateLabel}>
              <h2 className="font-semibold text-[#1E3F33]">{dateLabel}</h2>
              <div className="grid md:grid-cols-2 gap-5 mt-3">
                {items.map((r) => (
                  <Card key={r.id} className="p-6 rounded-3xl border border-[#E8E1D5] bg-white hover:-translate-y-1 transition-all hb-shadow-soft">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="flex items-center gap-2 text-[#7A7A7A] text-sm">
                          <FileText className="h-4 w-4" />{new Date(r.created_at).toLocaleDateString()}
                        </div>
                        <h3 className="font-display text-2xl mt-1 text-[#1A1A1A]">{r.summary?.slice(0, 90)}...</h3>
                      </div>
                      <Badge className={`rounded-full border font-semibold ${
                        r.severity === 'low' ? 'bg-[#E8F5E9] text-[#2A5244] border-[#2A5244]/20'
                        : r.severity === 'moderate' ? 'bg-[#FDECE4] text-[#E07A5F] border-[#E07A5F]/20'
                        : 'bg-[#FEE2E2] text-[#D9534F] border-[#D9534F]/20'}`}>
                        {r.severity}
                      </Badge>
                    </div>
                    <div className="mt-4 flex gap-2">
                      <Link to={`/report/${r.id}`}>
                        <Button variant="outline" className="rounded-full border-2 border-[#1E3F33] text-[#1E3F33] font-semibold">
                          {t(lang, 'view')} <ArrowRight className="h-4 w-4 ml-1" />
                        </Button>
                      </Link>
                      <Button onClick={() => openPdf(`/report/${r.id}/pdf`, `health-buddy-${r.id}.pdf`)} variant="ghost" className="rounded-full font-semibold text-[#1E3F33]">
                        <Download className="h-4 w-4 mr-1.5" /> PDF
                      </Button>
                    </div>
                  </Card>
                ))}
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}
